import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Chart from 'react-apexcharts';
import { FaTimes, FaSyncAlt, FaChartLine } from 'react-icons/fa';
import { request } from '../../services/api';

const ArchiveChartModal = ({
    isOpen,
    onClose,
    serial,
    sensorKey,
    title,
    unit
}) => {
    const [range, setRange] = useState('-24h');
    const [limit, setLimit] = useState(1000);
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const loadArchive = async () => {
        if (!serial || !sensorKey) {
            setError(`اطلاعات سنسور کامل نیست. serial=${serial || 'خالی'} sensor=${sensorKey || 'خالی'}`);
            setRecords([]);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const json = await request(
                `/get_sensor_archive.php?serial=${encodeURIComponent(serial)}&sensor=${encodeURIComponent(sensorKey)}&range=${encodeURIComponent(range)}&limit=${encodeURIComponent(limit)}`
            );

            if (!json || json.status !== 'success') {
                throw new Error(json?.message || 'خطا در دریافت داده نمودار');
            }

            const sorted = [...(json.records || [])]
                .filter(row => row.time && row.value !== null && row.value !== undefined)
                .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

            setRecords(sorted);
        } catch (err) {
            console.error('Archive chart error:', err);
            setError(err.message || 'خطای نامشخص');
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadArchive();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, serial, sensorKey, range, limit]);

    if (!isOpen) return null;

    const chartData = records
        .map(row => ({
            x: new Date(row.time).getTime(),
            y: Number(row.value)
        }))
        .filter(point => !Number.isNaN(point.x) && !Number.isNaN(point.y));

    const series = [
        {
            name: title || sensorKey,
            data: chartData
        }
    ];

   const isLongRange = ['-7d', '-30d', '-90d'].includes(range);

const formatPersianDateTime = (timestamp) => {
    try {
        return new Intl.DateTimeFormat('fa-IR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date(timestamp));
    } catch {
        return '';
    }
};

const formatPersianTime = (timestamp) => {
    try {
        return new Intl.DateTimeFormat('fa-IR', {
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date(timestamp));
    } catch {
        return '';
    }
};

const formatPersianAxisLabel = (timestamp) => {
    if (isLongRange) {
        return formatPersianDateTime(timestamp);
    }

    return formatPersianTime(timestamp);
};

    const options = {
        chart: {
            type: 'line',
            height: 420,
            background: 'transparent',
            foreColor: '#ecf0f1',
            toolbar: {
                show: true
            },
            zoom: {
                enabled: true,
                type: 'x',
                autoScaleYaxis: true
            },
            animations: {
                enabled: false
            }
        },
        stroke: {
            curve: 'stepline',
            width: 2
        },
        markers: {
    size: 0,
    strokeWidth: 0,
    hover: {
        size: 3
    }
},
        dataLabels: {
            enabled: false
        },
        xaxis: {
    type: 'datetime',
    labels: {
        datetimeUTC: false,
        rotate: -35,
        trim: true,
        style: {
            colors: '#bdc3c7',
            fontSize: '11px'
        },
        formatter: function (value, timestamp) {
            return formatPersianAxisLabel(timestamp || value);
        }
    }
},
        yaxis: {
            title: {
                text: unit ? `مقدار (${unit})` : 'مقدار',
                style: {
                    color: '#bdc3c7'
                }
            },
            labels: {
                style: {
                    colors: '#bdc3c7'
                }
            }
        },
        tooltip: {
    x: {
        formatter: function (value) {
            return formatPersianDateTime(value);
        }
    },
    y: {
        formatter: (val) => `${val}${unit ? ` ${unit}` : ''}`
    }
},
        grid: {
            borderColor: 'rgba(255,255,255,0.12)'
        },
        noData: {
            text: loading ? 'در حال دریافت...' : 'داده‌ای برای نمایش وجود ندارد',
            align: 'center',
            verticalAlign: 'middle',
            style: {
                color: '#bdc3c7',
                fontSize: '14px'
            }
        }
    };

    return createPortal(
        <div style={overlayStyle}>
            <div style={modalStyle}>
                <div style={headerStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaChartLine color="#f1c40f" />
                        <strong>نمودار آرشیو {title || sensorKey}</strong>
                    </div>

                    <button onClick={onClose} style={iconButtonStyle}>
                        <FaTimes />
                    </button>
                </div>

                <div style={toolbarStyle}>
                    <div style={{ ...toolbarItemStyle, justifyContent: 'flex-start' }}>
                        <div style={filterItemStyle}>
                            <span>بازه:</span>
                            <select
                                value={range}
                                onChange={(e) => setRange(e.target.value)}
                                style={selectStyle}
                            >
                                <option value="-1h">۱ ساعت اخیر</option>
                                <option value="-6h">۶ ساعت اخیر</option>
                                <option value="-12h">۱۲ ساعت اخیر</option>
                                <option value="-24h">۲۴ ساعت اخیر</option>
                                <option value="-7d">۷ روز اخیر</option>
                                <option value="-30d">۳۰ روز اخیر</option>
                                <option value="-90d">۹۰ روز اخیر</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ ...toolbarItemStyle, justifyContent: 'center' }}>
                        <div style={filterItemStyle}>
                            <span>تعداد:</span>
                            <select
                                value={limit}
                                onChange={(e) => setLimit(Number(e.target.value))}
                                style={selectStyle}
                            >
                                <option value={200}>۲۰۰</option>
                                <option value={500}>۵۰۰</option>
                                <option value={1000}>۱۰۰۰</option>
                                <option value={2000}>۲۰۰۰</option>
                                <option value={5000}>۵۰۰۰</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ ...toolbarItemStyle, justifyContent: 'flex-end' }}>
                        <button onClick={loadArchive} style={refreshButtonStyle} disabled={loading}>
                            <FaSyncAlt />
                            {loading ? 'در حال دریافت...' : 'بروزرسانی'}
                        </button>
                    </div>
                </div>

                <div style={infoStyle}>
                    <span style={{ ...infoItemStyle, textAlign: 'right' }}>دستگاه: {serial}</span>
                    <span style={{ ...infoItemStyle, textAlign: 'center' }}>سنسور: {sensorKey}</span>
                    <span style={{ ...infoItemStyle, textAlign: 'left' }}>تعداد رکورد: {records.length}</span>
                </div>

                {error && (
                    <div style={errorStyle}>
                        {error}
                    </div>
                )}

                <div style={chartWrapperStyle}>
                    <Chart
                        options={options}
                        series={series}
                        type="line"
                        height={420}
                    />
                </div>
            </div>
        </div>,
        document.body
    );
};

const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.65)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
};

const modalStyle = {
    width: 'min(1000px, 95vw)',
    maxHeight: '90vh',
    background: '#1f2d3a',
    color: '#fff',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.08)'
};

const headerStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 48px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: '#17212b',
    direction: 'rtl',
    textAlign: 'center'
};

const iconButtonStyle = {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    border: 'none',
    background: 'transparent',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '16px'
};

const toolbarStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '18px',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    direction: 'rtl'
};

const toolbarItemStyle = {
    flex: '1 1 0',
    display: 'flex'
};

const filterItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    direction: 'rtl'
};

const selectStyle = {
    background: '#101820',
    color: '#fff',
    border: '1px solid #34495e',
    borderRadius: '6px',
    padding: '6px 8px',
    outline: 'none'
};

const refreshButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '7px 12px',
    cursor: 'pointer'
};

const infoStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '18px',
    padding: '10px 16px',
    fontSize: '12px',
    color: '#bdc3c7',
    direction: 'rtl',
    textAlign: 'right'
};

const infoItemStyle = {
    flex: '1 1 0'
};

const errorStyle = {
    margin: '0 16px 10px',
    padding: '10px',
    background: 'rgba(231, 76, 60, 0.15)',
    border: '1px solid rgba(231, 76, 60, 0.4)',
    color: '#ffb3b3',
    borderRadius: '6px',
    fontSize: '13px'
};

const chartWrapperStyle = {
    padding: '16px',
    direction: 'ltr'
};

export default ArchiveChartModal;